import { Helmet } from 'react-helmet-async';

const StyleGuide = () => {
  const colors = [
    { name: "Background", hex: "#FAFAF8", usage: "Primary page background" },
    { name: "Navy (Primary)", hex: "#002178", usage: "Headers, CTAs, trust sections" },
    { name: "Grey (Alt)", hex: "#f5f5f0", usage: "Testimonials, Slack section backgrounds" },
    { name: "Text Dark", hex: "#1a1a1a", usage: "Body text, headings" },
    { name: "Text Muted", hex: "rgba(0,0,0,0.5)", usage: "Captions, secondary text" },
  ];

  const sectionFlow = [
    { section: "Header (Fixed)", bg: "Background + blur", text: "Dark" },
    { section: "Hero", bg: "Background (#FAFAF8)", text: "Dark + Navy accent" },
    { section: "Stats Strip", bg: "Navy (#002178)", text: "White" },
    { section: "Problems", bg: "Background (#FAFAF8)", text: "Dark" },
    { section: "Testimonials", bg: "Grey (#f5f5f0)", text: "Dark + Navy accent" },
    { section: "Assessment CTA", bg: "Navy (#002178)", text: "White" },
    { section: "Slack Screenshots", bg: "Grey (#f5f5f0)", text: "Navy label" },
    { section: "Footer", bg: "Background (#FAFAF8)", text: "Dark/Muted" },
  ];

  const typography = [
    { 
      name: "Hero Headline", 
      size: "clamp(3rem, 12vw, 10rem)", 
      weight: "300 (Light) / 500 (Medium for accent)", 
      leading: "0.9",
      tracking: "-0.03em"
    },
    { 
      name: "Section Heading", 
      size: "clamp(2.5rem, 6vw, 4.5rem)", 
      weight: "300 (Light)", 
      leading: "1.1",
      tracking: "-0.02em"
    },
    { 
      name: "Card Title", 
      size: "1.5rem - 2rem", 
      weight: "500 (Medium)", 
      leading: "1.2",
      tracking: "normal"
    },
    { 
      name: "Body Text", 
      size: "1rem - 1.125rem", 
      weight: "400 (Regular)", 
      leading: "1.6",
      tracking: "normal"
    },
    { 
      name: "Label/Eyebrow", 
      size: "0.875rem", 
      weight: "500 (Medium)", 
      leading: "1",
      tracking: "0.3em (uppercase)"
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Header */}
      <header className="bg-[#002178] text-white py-12 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-4xl font-light mb-2">FractionalCPO Style Guide</h1>
          <p className="text-white/70">Design system v3.0 — Editorial minimalism</p>
          <p className="text-white/50 text-sm mt-2">
            Previous version: <a href="/style-guide-old" className="underline hover:text-white">v2.0</a>
          </p>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-8 py-16 space-y-20">
        
        {/* Design Philosophy */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Design Philosophy</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-6 text-[#1a1a1a]">
            Editorial minimalism with bold typography
          </h2>
          <p className="text-[#1a1a1a]/60 text-lg max-w-2xl leading-relaxed">
            Large, confident headlines. Generous whitespace. Restrained color palette. 
            Navy for authority, warm neutrals for approachability. Movement through scale, not decoration.
          </p>
        </section>

        {/* Section Flow */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Section Flow</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            Page rhythm
          </h2>
          
          <div className="flex flex-col rounded-lg overflow-hidden border border-black/10">
            {sectionFlow.map((row, i) => (
              <div 
                key={i}
                className={`p-4 flex justify-between items-center ${
                  row.bg.includes('Navy') ? 'bg-[#002178] text-white' : 
                  row.bg.includes('Grey') ? 'bg-[#f5f5f0] text-[#1a1a1a]' : 
                  'bg-[#FAFAF8] text-[#1a1a1a]'
                }`}
              >
                <span className="font-medium">{row.section}</span>
                <span className="text-sm opacity-70">{row.bg}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Color Palette */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Color Palette</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            Restrained & intentional
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors.map((color) => (
              <div key={color.name} className="border border-black/10 rounded-lg overflow-hidden">
                <div 
                  className="h-20"
                  style={{ backgroundColor: color.hex }}
                />
                <div className="p-4 bg-white">
                  <h3 className="font-medium text-[#1a1a1a] mb-1">{color.name}</h3>
                  <p className="text-sm text-[#1a1a1a]/50 font-mono mb-2">{color.hex}</p>
                  <p className="text-sm text-[#1a1a1a]/70">{color.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Typography</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            Scale creates hierarchy
          </h2>
          
          <div className="space-y-8">
            {typography.map((type) => (
              <div key={type.name} className="border-b border-black/10 pb-8">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="lg:w-1/2">
                    {type.name === "Hero Headline" && (
                      <p className="text-[clamp(2rem,8vw,4rem)] font-light leading-[0.9] tracking-[-0.03em] text-[#1a1a1a]">
                        Hero Headline
                      </p>
                    )}
                    {type.name === "Section Heading" && (
                      <p className="text-[clamp(1.5rem,4vw,2.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-[#1a1a1a]">
                        Section Heading
                      </p>
                    )}
                    {type.name === "Card Title" && (
                      <p className="text-2xl font-medium text-[#1a1a1a]">Card Title</p>
                    )}
                    {type.name === "Body Text" && (
                      <p className="text-lg text-[#1a1a1a]/80 leading-relaxed">
                        This is body text. Comfortable reading size with generous line height.
                      </p>
                    )}
                    {type.name === "Label/Eyebrow" && (
                      <p className="text-sm uppercase tracking-[0.3em] text-[#002178] font-medium">
                        Label / Eyebrow
                      </p>
                    )}
                  </div>
                  <div className="lg:w-1/2 bg-[#f5f5f0] p-4 rounded-lg text-sm text-[#1a1a1a]/70 space-y-1">
                    <p><span className="font-medium text-[#1a1a1a]">{type.name}</span></p>
                    <p>Size: {type.size}</p>
                    <p>Weight: {type.weight}</p>
                    <p>Line height: {type.leading}</p>
                    <p>Letter spacing: {type.tracking}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Buttons</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            CTAs
          </h2>
          
          <div className="space-y-8">
            {/* Primary on Light */}
            <div className="p-8 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-4">Primary (Light Background)</h3>
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <button className="px-8 py-4 bg-[#002178] text-white font-medium transition-transform hover:scale-105">
                  Book a Strategy Call
                </button>
                <div className="text-sm text-[#1a1a1a]/70 space-y-1">
                  <p>Padding: px-8 py-4</p>
                  <p>Font: Medium weight</p>
                  <p>Shape: Sharp corners (no border-radius)</p>
                  <p>Hover: scale-105</p>
                  <p>Case: Title Case</p>
                </div>
              </div>
            </div>

            {/* Primary on Dark */}
            <div className="p-8 bg-[#002178] rounded-lg">
              <h3 className="font-medium text-white mb-4">Primary (Dark Background)</h3>
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <button className="px-8 py-4 bg-white text-[#002178] font-medium hover:bg-white/90 transition-colors">
                  Start your assessment
                </button>
                <div className="text-sm text-white/70 space-y-1">
                  <p>Inverted colors</p>
                  <p>Hover: bg-white/90</p>
                  <p>Same dimensions as light variant</p>
                </div>
              </div>
            </div>

            {/* Text Link */}
            <div className="p-8 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-4">Text Link (Header)</h3>
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <span className="text-sm font-medium text-[#1a1a1a] hover:text-[#002178] transition-colors cursor-pointer">
                  Get in touch →
                </span>
                <div className="text-sm text-[#1a1a1a]/70 space-y-1">
                  <p>Font: 14px, Medium</p>
                  <p>Hover: Navy color</p>
                  <p>Arrow suffix for navigation hints</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Layout */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Layout</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            Container & spacing
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-3">Container</h3>
              <ul className="text-sm text-[#1a1a1a]/70 space-y-2">
                <li>• Max width: <code className="bg-white px-1 rounded">max-w-[1400px]</code></li>
                <li>• Padding: <code className="bg-white px-1 rounded">px-6 md:px-12</code></li>
                <li>• Centered: <code className="bg-white px-1 rounded">mx-auto</code></li>
              </ul>
            </div>
            
            <div className="p-6 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-3">Section Spacing</h3>
              <ul className="text-sm text-[#1a1a1a]/70 space-y-2">
                <li>• Large sections: <code className="bg-white px-1 rounded">py-24 md:py-40</code></li>
                <li>• Medium sections: <code className="bg-white px-1 rounded">py-16 md:py-24</code></li>
                <li>• Small sections: <code className="bg-white px-1 rounded">py-8 md:py-12</code></li>
              </ul>
            </div>

            <div className="p-6 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-3">Grid</h3>
              <ul className="text-sm text-[#1a1a1a]/70 space-y-2">
                <li>• Stats: 4-column on desktop, 2 on mobile</li>
                <li>• Two-column layouts with sticky sidebars</li>
                <li>• Gap: <code className="bg-white px-1 rounded">gap-16 md:gap-24</code></li>
              </ul>
            </div>

            <div className="p-6 bg-[#f5f5f0] rounded-lg">
              <h3 className="font-medium text-[#1a1a1a] mb-3">Animation</h3>
              <ul className="text-sm text-[#1a1a1a]/70 space-y-2">
                <li>• Scroll-triggered fade/slide (Framer Motion)</li>
                <li>• Button hover: scale-105</li>
                <li>• Smooth transitions: 200-300ms</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section>
          <p className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium">Forms</p>
          <h2 className="text-[clamp(2rem,4vw,3rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8 text-[#1a1a1a]">
            Contact modal inputs
          </h2>
          
          <div className="p-8 bg-[#002178] rounded-lg max-w-md">
            <div className="space-y-4">
              <input 
                type="text" 
                placeholder="Name"
                className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 px-5 h-12 text-base outline-none"
              />
              <input 
                type="email" 
                placeholder="Email"
                className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 px-5 h-12 text-base outline-none"
              />
              <textarea 
                placeholder="How can we help?"
                className="w-full bg-transparent text-white placeholder:text-white/40 border border-white/20 focus:border-white/50 px-5 py-4 min-h-[100px] text-base resize-none outline-none"
              />
            </div>
            <p className="text-white/50 text-sm mt-4">
              Sharp corners, subtle borders, placeholder-only labels
            </p>
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-[#002178] text-white py-8 px-8 text-center">
        <p className="text-white/60">FractionalCPO Design System v3.0</p>
      </footer>
    </div>
  );
};

export default StyleGuide;
